import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/joy";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { gradientPairs } from "../../../theme";
import GuestNavbar from "../../../components/Layout/GuestNavbar";

const HERO_BG = "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)";
const PINK_ALERT_BG = "rgba(244, 114, 182, 0.12)";
const PINK_ALERT_BORDER = "rgba(244, 114, 182, 0.35)";
const INFO_ICON_BG = "#3b82f6";

const InfoBlock = ({
    children,
    highlight,
}: {
    children: React.ReactNode;
    highlight?: boolean;
}) => (
    <Card
        variant="outlined"
        sx={{
            padding: 2,
            borderRadius: 2,
            height: "100%",
            position: "relative",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            ...(highlight && {
                border: "2px solid",
                borderColor: "#f87171",
            }),
        }}
        data-no-glow
        data-no-animation
    >
        <Box
            sx={{
                position: "absolute",
                top: 12,
                left: 12,
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: INFO_ICON_BG,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Info size={16} />
        </Box>
        <Box sx={{ pl: 4 }}>{children}</Box>
    </Card>
);

const Tcf = () => {
    const navigate = useNavigate();

    return (
        <Stack sx={{ minHeight: "100vh", overflowX: "hidden", bgcolor: "background.body" }}>
            <GuestNavbar />

            {/* Hero banner */}
            <Box
                sx={{
                    width: "100%",
                    paddingY: 5,
                    paddingX: 2,
                    background: HERO_BG,
                    backgroundImage: `
                        ${HERO_BG},
                        radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                        radial-gradient(circle at 80% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)
                    `,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography
                    level="h1"
                    sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                        fontWeight: 700,
                        color: "white",
                        textAlign: "center",
                        letterSpacing: "-0.02em",
                    }}
                >
                    TCF Canada & TCF Public
                </Typography>
            </Box>

            {/* Main content */}
            <Stack
                paddingX={{ xs: 2, sm: 4, lg: 6 }}
                paddingY={4}
                gap={4}
                maxWidth={1000}
                margin="0 auto"
                width="100%"
                sx={{ bgcolor: "background.surface" }}
            >
                {/* Pink alert - Important notice */}
                <Alert
                    variant="soft"
                    sx={{
                        bgcolor: PINK_ALERT_BG,
                        border: "1px solid",
                        borderColor: PINK_ALERT_BORDER,
                        borderRadius: 2,
                        padding: 2.5,
                        "& .JoyAlert startDecorator": { display: "none" },
                    }}
                >
                    <Typography level="body-md" textColor="neutral.700">
                        <strong>Important information.</strong> The TCF and TCF Canada are official French
                        proficiency tests offered by France Éducation International (FEI). Exam sessions
                        and registration are managed by authorised test centres (e.g. Alliance Française).
                        LinguaScore is a preparation platform: we help you get ready with practice materials
                        and mock tests aligned to the exam format. For exam dates, fees and registration,
                        contact your local test centre.
                    </Typography>
                </Alert>

                {/* TCF logo + intro row */}
                <Stack direction={{ xs: "column", md: "row" }} gap={3} alignItems={{ md: "flex-start" }}>
                    <Box
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: "#6366f1",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "1.25rem",
                                fontWeight: 700,
                            }}
                        >
                            TCF
                        </Box>
                        <Typography
                            level="body-sm"
                            sx={{
                                color: "#0ea5e9",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.02em",
                                maxWidth: 160,
                            }}
                        >
                            Test de Connaissance du Français
                        </Typography>
                    </Box>
                    <Typography level="body-md" textColor="neutral.600" sx={{ flex: 1 }}>
                        The <strong>Test de Connaissance du Français (TCF)</strong> is a reliable assessment
                        tool offered by <strong>France Éducation International</strong> for evaluating your
                        proficiency in French. It is designed for non-native French speakers seeking to
                        assess and certify their French language skills for professional, personal or
                        academic purposes. The test is suitable for individuals aged 16 and above. For
                        immigration inquiries, please visit the CIC website.
                    </Typography>
                </Stack>

                {/* TCF Canada + TCF Tout Public two-column cards */}
                <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                        <Card
                            variant="outlined"
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                                height: "100%",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            }}
                            data-no-glow
                            data-no-animation
                        >
                            <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 1.5 }}>
                                TCF Canada
                            </Typography>
                            <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 2 }}>
                                TCF Canada is a variation of the TCF comprising <strong>four mandatory tests</strong> assessing
                                general French language skills. It is used for Canadian immigration and citizenship.
                            </Typography>
                            <Typography level="title-sm" sx={{ marginBottom: 1 }}>
                                Four mandatory tests (2h47 total):
                            </Typography>
                            <List size="sm" sx={{ "--ListMarkerSize": "6px", marginBottom: 1 }}>
                                <ListItem><strong>Listening comprehension:</strong> 35 minutes, 39 multiple-choice questions</ListItem>
                                <ListItem><strong>Reading comprehension:</strong> 60 minutes, 39 multiple-choice questions</ListItem>
                                <ListItem><strong>Written skills:</strong> 60 minutes, 3 exercises (computer-based)</ListItem>
                                <ListItem><strong>Verbal skills:</strong> 12 minutes (incl. 2 min preparation), 3 exercises, one-on-one with examiner</ListItem>
                            </List>
                        </Card>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <Card
                            variant="outlined"
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                                height: "100%",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            }}
                            data-no-glow
                            data-no-animation
                        >
                            <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 1.5 }}>
                                TCF Tout Public
                            </Typography>
                            <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 2 }}>
                                The <strong>TCF Tout Public</strong> includes five mandatory tests and is offered on select
                                dates by authorised centres. It is used for general certification of French level.
                            </Typography>
                            <Typography level="body-sm" textColor="neutral.600">
                                For more information or to register for other TCF versions (e.g. TCF IRN, TCF Québec),
                                please contact your local test centre or France Éducation International.
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Important Information - grid of info blocks */}
                <Box>
                    <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 1.5 }}>
                        All tests are completed on computers except the oral expression test, which is with an examiner.
                    </Typography>
                    <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 2 }}>
                        Important Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock highlight>
                                <Typography level="body-sm" textColor="neutral.700">
                                    The exam takes place at your chosen test centre. Confirm the exact
                                    address and room on your official invitation.
                                </Typography>
                                <Typography level="body-sm" sx={{ marginTop: 1, color: "#dc2626", fontWeight: 700 }}>
                                    It is your responsibility to verify the address on your official invitation.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    Your certificate will be <strong>valid for 2 years</strong>; this date is
                                    indicated on the certificate. You may take the TCF as many times as you wish,
                                    with a mandatory waiting period of at least <strong>20 days</strong> between
                                    two takes in any location.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    You will get results within <strong>6 weeks</strong>. Result certificates
                                    are no longer sent by the examination centre. <strong>France Éducation
                                    International (FEI)</strong> sends an email inviting you to create an account
                                    on the <strong>TEO+ platform</strong> and download your certificate online.
                                    Check your spam/junk folder and use a valid email when registering.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    The exam (written and oral comprehension, written expression) is{" "}
                                    <strong>computer-based</strong>. The oral expression test is done in person
                                    with an examiner. French accents are available on the FEI platform.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    A few days before your exam, you will receive an official invitation with
                                    your schedule. It is essential you arrive at the time indicated; you will not
                                    be granted access to the room after the examination starts.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    Test centres are not liable for final grades. Reassessment by FEI may be
                                    requested within 25 days of receiving your attestation; a fee per section
                                    may apply. Contact your test centre for the required form and information.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    The organisation of TCF exams requires the collaboration of France Éducation
                                    International (FEI). In very rare cases, unforeseen technical issues may
                                    lead to session changes or cancellations. Test centres cannot be held
                                    responsible for travel or accommodation expenses. We encourage you to
                                    register at an authorised centre close to your place of residence when
                                    possible.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                    </Grid>
                </Box>

                {/* Exam Day Guidelines */}
                <Card
                    variant="outlined"
                    sx={{ padding: 3, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                    data-no-glow
                    data-no-animation
                >
                    <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 2 }}>
                        Exam Day Guidelines
                    </Typography>
                    <List
                        component="ol"
                        size="sm"
                        sx={{ listStyle: "decimal", pl: 2.5, "--ListMarkerSize": "1.5em", gap: 0.5 }}
                    >
                        <ListItem>
                            <strong>Timely check-in:</strong> Present a valid ID (passport, permanent
                            resident card, or driver&apos;s license) and your schedule notification. Without
                            valid ID, entry is denied. Late arrivals are not allowed; no refunds or
                            deferments.
                        </ListItem>
                        <ListItem>
                            <strong>ID verification and documentation:</strong> After the ID check, you will
                            sign the required documents for each test and have an identification photo taken.
                            You may then be asked to wait in a designated area.
                        </ListItem>
                        <ListItem>
                            <strong>Entering the exam room:</strong> You will be guided to the room. Personal
                            items such as phones and watches must be stored away.
                        </ListItem>
                        <ListItem>
                            <strong>Pre-test briefing:</strong> The supervisor will provide final instructions.
                        </ListItem>
                        <ListItem>
                            <strong>Exam format:</strong> Written and oral comprehension and written expression
                            are computer-based. Oral expression is with an examiner. French accents are
                            accessible through the FEI platform.
                        </ListItem>
                        <ListItem>
                            <strong>Conduct:</strong> No exits or communication between candidates are
                            allowed, except for medically documented emergencies. Latecomers will be denied
                            entry.
                        </ListItem>
                        <ListItem>
                            <strong>Misconduct:</strong> Fraudulent or inappropriate behaviour may result in
                            immediate removal and potential future test bans, as decided by France Éducation
                            International.
                        </ListItem>
                        <ListItem>
                            <strong>Results:</strong> Official certificates are sent by email within 6 weeks
                            post-exam (via FEI / TEO+ platform).
                        </ListItem>
                    </List>
                </Card>

                {/* CTA - Prepare with LinguaScore */}
                <Card
                    variant="soft"
                    sx={{
                        padding: 3,
                        borderRadius: 2,
                        bgcolor: "primary.50",
                        borderColor: "primary.200",
                    }}
                    data-no-glow
                    data-no-animation
                >
                    <Typography level="title-lg" sx={{ marginBottom: 1 }}>
                        Prepare with LinguaScore
                    </Typography>
                    <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 2 }}>
                        Get ready for the TCF and TCF Canada with practice materials, mock tests and
                        progress tracking. Our content is designed to match the exam format and help you
                        reach your target score.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                        <Button
                            size="lg"
                            onClick={() => navigate("/register")}
                            sx={{
                                background: `linear-gradient(to right, ${gradientPairs[0][0]}, ${gradientPairs[0][1]})`,
                            }}
                        >
                            Start preparing
                        </Button>
                        <Button size="lg" variant="outlined" onClick={() => navigate("/pricing")}>
                            View pricing
                        </Button>
                    </Stack>
                </Card>
            </Stack>
        </Stack>
    );
};

export default Tcf;
